/**
 * Componente de botón de información
 * Muestra tutorial de instrucciones en español
 */

import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useScreenOrientation } from '../../hooks/useScreenOrientation';
import { LottieAvatar } from '../Avatar/LottieAvatar';
import authService from '../../services/authService';
import './InfoButton.css';

interface InfoButtonProps {
  onToggle?: (isVisible: boolean) => void;
}

export function InfoButton({ onToggle }: InfoButtonProps) {
  const [isInformationVisible, setIsInformationVisible] = useState(false);
  const orientation = useScreenOrientation();
  const panelRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const instructionsRef = useRef<HTMLDivElement>(null);

  const toggleInformation = () => {
    const newState = !isInformationVisible;
    setIsInformationVisible(newState);
    onToggle?.(newState);
  };

  // Determinar el role del usuario para mostrar la explicación correcta
  const apolearnData = authService.getApolearnUserData();
  const userRole = apolearnData?.role || null;
  const roleLower = userRole?.toLowerCase() || '';
  const isUndefined = userRole === 'Undefined' || userRole === 'undefined';
  const isDocente = !isUndefined && (roleLower === 'teacher' || roleLower.includes('docente') || roleLower.includes('instructor') || roleLower.includes('profesor'));
  
  const activitiesExplanation = isDocente 
    ? "Presiona para crear, editar y gestionar actividades para tus estudiantes."
    : "Presiona para ver y ejecutar las actividades asignadas por tu profesor.";


  return (
    <>
      <button
        className={`info-button ${isInformationVisible ? 'hidden' : ''}`}
        onClick={toggleInformation}
        aria-label="Mostrar información"
      >
        <img 
          src="/assets/ui/iconos/Información.png" 
          alt="Información" 
          className="info-icon"
        />
      </button>

      {isInformationVisible && createPortal(
        <div className={`information-menu ${orientation}`}>
          {orientation === 'landscape' && (
            <div 
              className="menu-background"
              onClick={toggleInformation}
            />
          )}
          
          {orientation === 'landscape' ? (
            <div className="tutorial-panel-horizontal">
              <button 
                className="close-button"
                onClick={toggleInformation}
                aria-label="Cerrar"
              >
                <img 
                  src="/assets/ui/botones/Boton_Cerrar.png" 
                  alt="Cerrar"
                  className="close-icon"
                />
              </button>
              
              <div className="tutorial-instructions">
                <div className="instruction-item">
                  <div className="instruction-icon">
                    <img src="/assets/ui/iconos/Mic.png" alt="Micrófono" />
                  </div>
                  <p className="instruction-text">Presiona para activar el micrófono e iniciar la grabación.</p>
                </div>
                
                <div className="instruction-item">
                  <div className="instruction-icon">
                    <img src="/assets/ui/chat/Chat_Orange.png" alt="Chat" />
                  </div>
                  <p className="instruction-text">Presiona para mostrar u ocultar el chat.</p>
                </div>
                
                <div className="instruction-item">
                  <div className="instruction-icon">
                    <img src="/assets/ui/iconos/Enviar AF.png" alt="Enviar" />
                  </div>
                  <p className="instruction-text">Presiona para enviar tu mensaje.</p>
                </div>
                
                <div className="instruction-item">
                  <div className="instruction-icon">
                    <img src="/assets/ui/iconos/Audio.png" alt="Audio" />
                  </div>
                  <p className="instruction-text">Presiona para enviar el audio.</p>
                </div>
                
                <div className="instruction-item">
                  <div className="instruction-icon">
                    <img src="/assets/ui/iconos/Progreso.png" alt="Progreso" />
                  </div>
                  <p className="instruction-text">Presiona para ver tu progreso académico.</p>
                </div>
                
                <div className="instruction-item">
                  <div className="instruction-icon">
                    <img src="/assets/ui/iconos/Actividades.png" alt="Actividades" />
                  </div>
                  <p className="instruction-text">{activitiesExplanation}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="tutorial-panel-vertical" ref={panelRef}>
              <div className="tutorial-header-vertical" ref={headerRef}>
                <img 
                  src="/assets/ui/otros/galia.png" 
                  alt="Galia" 
                  className="tutorial-logo-top-left"
                />
                <img 
                  src="/assets/ui/otros/logo_af.png" 
                  alt="Alianza Francesa" 
                  className="tutorial-logo-top-right"
                />
                <button 
                  className="close-button-vertical-header"
                  onClick={toggleInformation}
                  aria-label="Cerrar"
                >
                  <img 
                    src="/assets/ui/botones/Boton_Cerrar.png" 
                    alt="Cerrar"
                    className="close-icon-vertical"
                  />
                </button>
                <div className="tutorial-avatar-container">
                  <LottieAvatar mode="conversacional" preview={false} avatarId="avatar1" />
                </div>
              </div>
              
              <div className="tutorial-content-vertical" ref={contentRef}>
                <div className="tutorial-instructions-vertical" ref={instructionsRef}>
                  <div className="instruction-item-vertical">
                    <div className="instruction-icon-vertical">
                      <img src="/assets/ui/iconos/Mic.png" alt="Micrófono" />
                    </div>
                    <p className="instruction-text-vertical">Presiona para activar el micrófono e iniciar la grabación.</p>
                  </div>
                  
                  <div className="instruction-item-vertical">
                    <div className="instruction-icon-vertical">
                      <img src="/assets/ui/chat/Chat_Orange.png" alt="Chat" />
                    </div>
                    <p className="instruction-text-vertical">Presiona para mostrar u ocultar el chat.</p>
                  </div>
                  
                  <div className="instruction-item-vertical">
                    <div className="instruction-icon-vertical">
                      <img src="/assets/ui/iconos/Enviar AF.png" alt="Enviar" />
                    </div>
                    <p className="instruction-text-vertical">Presiona para enviar tu mensaje.</p>
                  </div>
                  
                  <div className="instruction-item-vertical">
                    <div className="instruction-icon-vertical">
                      <img src="/assets/ui/iconos/Audio.png" alt="Audio" />
                    </div>
                    <p className="instruction-text-vertical">Presiona para enviar el audio.</p>
                  </div>
                  
                  <div className="instruction-item-vertical">
                    <div className="instruction-icon-vertical">
                      <img src="/assets/ui/iconos/Progreso.png" alt="Progreso" />
                    </div>
                    <p className="instruction-text-vertical">Presiona para ver tu progreso académico.</p>
                  </div>
                  
                  <div className="instruction-item-vertical">
                    <div className="instruction-icon-vertical">
                      <img src="/assets/ui/iconos/Actividades.png" alt="Actividades" />
                    </div>
                    <p className="instruction-text-vertical">{activitiesExplanation}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  );
}


